import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Save, Loader2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_TYPES = ["503A Pharmacy", "503B Outsourcing Facility", "Lab Partner", "Telehealth Partner",
  "Fulfillment Partner", "Medical Director Network", "Compliance/Legal Partner", "EHR/Software Partner", "Other"];

const STATUS_COLORS = {
  Active: "bg-emerald-50 text-emerald-700",
  Pending: "bg-amber-50 text-amber-700",
  Paused: "bg-slate-100 text-slate-600",
  Archived: "bg-red-50 text-red-600",
};

const empty = { partner_name: "", partner_type: "503A Pharmacy", contact_name: "", contact_email: "",
  contact_phone: "", website: "", states_served: [], services_offered: [], categories: [],
  minimum_volume: "", required_documentation: "", turnaround_time: "", partner_status: "Active", notes: "" };

export default function AdminVendors() {
  const { authHeader } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchVendors = () => {
    setLoading(true);
    axios.get(`${API}/vendors`, { headers: authHeader() })
      .then(r => setVendors(r.data.vendors || []))
      .finally(() => setLoading(false));
  };
  useEffect(fetchVendors, []);

  const openCreate = () => { setForm(empty); setEditing(null); setShowModal(true); };
  const openEdit = (v) => { setForm({ ...v }); setEditing(v.id); setShowModal(true); };

  const handleSave = async () => {
    if (!form.partner_name) return;
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/vendors/${editing}`, form, { headers: authHeader() });
      } else {
        await axios.post(`${API}/vendors`, form, { headers: authHeader() });
      }
      setShowModal(false);
      fetchVendors();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor partner?")) return;
    await axios.delete(`${API}/vendors/${id}`, { headers: authHeader() }).catch(() => {});
    fetchVendors();
  };

  return (
    <AdminLayout title="Vendor Partners">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">{vendors.length} partner{vendors.length !== 1 ? "s" : ""}</p>
          <button data-testid={ADMIN.addVendorBtn} onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 transition-all">
            <Plus size={16} /> Add Partner
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table data-testid={ADMIN.vendorsTable} className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Partner Name</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Type</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Contact</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500">Status</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : vendors.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">No vendor partners yet. Click "Add Partner" to get started.</td></tr>
              ) : vendors.map(v => (
                <tr key={v.id} className="hover:bg-slate-50/50">
                  <td className="px-6 py-4 font-medium text-slate-900">{v.partner_name}</td>
                  <td className="px-6 py-4 text-slate-500">{v.partner_type}</td>
                  <td className="px-6 py-4 text-slate-500">{v.contact_email || v.contact_name || "—"}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[v.partner_status] || "bg-slate-100 text-slate-600"}`}>
                      {v.partner_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(v)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(v.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">{editing ? "Edit Partner" : "Add Vendor Partner"}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Partner Name *</label>
                  <input value={form.partner_name} onChange={e => setForm({ ...form, partner_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Partner Type</label>
                  <select value={form.partner_type} onChange={e => setForm({ ...form, partner_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                    {PARTNER_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                  <select value={form.partner_status} onChange={e => setForm({ ...form, partner_status: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                    {["Active", "Pending", "Paused", "Archived"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Contact Name</label>
                  <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Contact Email</label>
                  <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Contact Phone</label>
                  <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Website</label>
                  <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                    placeholder="https://"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 text-sm border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !form.partner_name}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-all">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? "Saving..." : "Save Partner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
